/**
 * Timezone options for every timezone picker in the app.
 *
 * Replaces three hand-maintained lists that between them offered 9 or 13
 * zones. Neither list covered India, so admins picked "GMT" (Europe/London)
 * and adjusted the clock time by hand. Europe/London observes British Summer
 * Time, so those slots drifted by an hour for seven months of the year.
 *
 * The list comes from the runtime's own IANA database, so there is nothing to
 * maintain and it updates with the browser.
 *
 * Store the `value` verbatim. Postgres, luxon (medley-api) and Google Calendar
 * all accept these identifiers unchanged.
 */

/**
 * ICU still reports pre-rename canonical names from
 * `Intl.supportedValuesOf('timeZone')`. India comes back as `Asia/Calcutta`,
 * the pre-2001 spelling, because ICU treats `Asia/Kolkata` as an alias.
 *
 * Map them to the modern IANA name for both display and storage. Verified
 * that Postgres `AT TIME ZONE`, `pg_timezone_names`, luxon 3.7.2 and
 * `Intl.DateTimeFormat` all accept the modern name and return the same
 * offset.
 *
 * Keys must exist in the ICU list to be useful. `Asia/Chongqing` and
 * `Europe/Nicosia` are absent on Node 20 / current browsers, so they are
 * deliberately not listed here.
 */
const IANA_RENAMES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Europe/Kiev': 'Europe/Kyiv',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
  'America/Godthab': 'America/Nuuk',
};

/**
 * Zones whose identifier wins when several collapse into one option.
 *
 * ICU calls fifteen zones "Eastern Time". They become a single option, and
 * this list decides that the option stores `America/New_York` rather than
 * `America/Detroit`. Listing every value production already holds means no
 * stored row is ever rewritten to a different city. Seeded from the 2026-09-04
 * audit: the 8 distinct values in `coach_profiles.home_base_timezone` and the
 * 5 in `time_slots.timezone`.
 *
 * This is no longer the list a picker offers. ALLOWED_ZONES is. Entries here
 * that are not in ALLOWED_ZONES, such as `Asia/Tokyo`, stay resolvable so a
 * row already holding them still renders its proper label.
 */
export const COMMON_ZONES: readonly string[] = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
];

/**
 * The only zones a picker offers, in the order the team asked for them.
 *
 * Curated by Jess on 2026-09-08 after a session scheduled for 9:00 AM arrived
 * in a calendar at 10:00 AM.
 *
 * **The bug this list fixes.** The full IANA list contains no fixed-offset
 * pseudo-zones: `Intl.supportedValuesOf('timeZone')` returns 417 entries and
 * every one of them is a real `Region/City`. The trap was subtler. Seven
 * options read as a region's own time while never observing daylight saving,
 * because they name a place inside that region's band that keeps one offset
 * all year:
 *
 * | Looks like       | Is really              | Offset      |
 * |------------------|------------------------|-------------|
 * | Eastern Standard | `America/Cancun`       | -05:00 always |
 * | Central Standard | `America/Mexico_City`  | -06:00 always |
 * | Mountain Standard| `America/Phoenix`      | -07:00 always |
 * | Atlantic Standard| `America/Anguilla`     | -04:00 always |
 * | Aus. Eastern Std | `Australia/Brisbane`   | +10:00 always |
 * | Aus. Central Std | `Australia/Darwin`     | +09:30 always |
 * | Central Eur. Std | `Africa/Algiers`       | +01:00 always |
 *
 * Picking `America/Cancun` and meaning New York pins the session to UTC-5, so
 * 9:00 AM lands at 10:00 AM for an Eastern viewer through the summer and
 * 9:00 AM through the winter. That is exactly the report.
 *
 * Restricting to this list removes all seven in one move. `America/Phoenix`
 * stays because Arizona genuinely never changes its clock, but it is relabelled
 * so it can never again be mistaken for Mountain Time. See LABEL_OVERRIDES.
 *
 * **The rule for editing this list.** Every entry must be the zone the people
 * in that region actually live in, so the clock they picked is the clock they
 * keep across a daylight saving transition. Never add a zone whose name is the
 * ` Standard Time` twin of another entry. `timezones.test.ts` fails the build
 * if you do.
 *
 * A value already stored outside this list still renders in its field, so
 * shortening the list never blanks an existing row. It only stops being a new
 * choice.
 */
export const ALLOWED_ZONES: readonly string[] = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'Asia/Kolkata',
  'Australia/Sydney',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Europe/London',
  'Europe/Paris',
];

/**
 * Names to show instead of the one ICU reports.
 *
 * Kept to the two cases where the ICU name actively misleads. Applied after
 * grouping, so it changes only what a user reads, never which zones collapse
 * together or what gets stored.
 */
const LABEL_OVERRIDES: Record<string, string> = {
  // ICU calls this "Mountain Standard Time", which is the trap. Arizona is not
  // Mountain Time: it stays at -07:00 while Denver moves to -06:00 for eight
  // months. Naming the state is the only label an admin cannot misread.
  'America/Phoenix': 'Arizona Time',
  // ICU says "Hong Kong Standard Time". Honest, since Hong Kong never changes
  // its clock, but the team asked for "Hong Kong time" and dropping the word
  // keeps "Standard" meaning one thing across the list.
  'Asia/Hong_Kong': 'Hong Kong Time',
};

/**
 * Words that should find a zone beyond its label, cities and identifier.
 *
 * Britain is the reason this exists. The team's list named both GMT and BST,
 * which are the winter and summer names of one zone, `Europe/London`. Offering
 * both would rebuild the standard-versus-daylight pair this change removes, so
 * there is one entry. Both words still find it.
 */
const EXTRA_SEARCH_TERMS: Record<string, string> = {
  'Europe/London': 'GMT BST UK Britain England Scotland Wales British',
  'America/Phoenix': 'Arizona AZ MST Phoenix Tucson',
  'Asia/Shanghai': 'China CST PRC Mainland',
  'Australia/Sydney': 'AET AEST AEDT Australia',
  'Asia/Kolkata': 'IST India Bharat',
  'Europe/Paris': 'CET CEST Europe',
};

/**
 * Zones `Intl.supportedValuesOf('timeZone')` does not return at all.
 *
 * ICU emits no `Etc/*` entries and no bare `UTC`, yet production already holds
 * `UTC` in `users.timezone` (532 rows) and `sessions.timezone` (370 rows). A
 * list built only from ICU could not represent a value already in the
 * database, so seed these explicitly. `Intl`, Postgres and luxon all accept
 * them.
 *
 * Seeded here rather than in COMMON_ZONES on purpose. That keeps `UTC`
 * selectable, so a stored value always renders, without putting it in front of
 * an admin picking a real market.
 */
const EXTRA_ZONES: readonly string[] = ['UTC'];

/**
 * Extra city names that should find a timezone, mapped to a zone in that city.
 *
 * The IANA database names only one city per zone, so "Asia/Kolkata" exists but
 * "Mumbai" and "New Delhi" do not, and "America/New_York" exists but "Boston"
 * and "Atlanta" do not. An admin typing their own city would get
 * "No timezone found" for 41 of the 59 largest business cities.
 *
 * These are search terms only. They never become a stored value: each one
 * resolves through the same grouping as everything else, so "Frankfurt"
 * selects the same option as "Paris".
 */
const CITY_ALIASES: Record<string, string> = {
  // India. The zone this whole change exists to make available.
  'New Delhi': 'Asia/Kolkata',
  Delhi: 'Asia/Kolkata',
  Mumbai: 'Asia/Kolkata',
  Bombay: 'Asia/Kolkata',
  Bangalore: 'Asia/Kolkata',
  Bengaluru: 'Asia/Kolkata',
  Hyderabad: 'Asia/Kolkata',
  Chennai: 'Asia/Kolkata',
  Pune: 'Asia/Kolkata',
  Gurgaon: 'Asia/Kolkata',
  Gurugram: 'Asia/Kolkata',
  Noida: 'Asia/Kolkata',
  Ahmedabad: 'Asia/Kolkata',
  // Greater China
  Beijing: 'Asia/Shanghai',
  Shenzhen: 'Asia/Shanghai',
  Guangzhou: 'Asia/Shanghai',
  // Rest of APAC
  'Tel Aviv': 'Asia/Jerusalem',
  Osaka: 'Asia/Tokyo',
  Kyoto: 'Asia/Tokyo',
  Yokohama: 'Asia/Tokyo',
  Hanoi: 'Asia/Ho_Chi_Minh',
  Doha: 'Asia/Qatar',
  'Abu Dhabi': 'Asia/Dubai',
  // United States, eastern
  Boston: 'America/New_York',
  Atlanta: 'America/New_York',
  Miami: 'America/New_York',
  Philadelphia: 'America/New_York',
  Washington: 'America/New_York',
  'Washington DC': 'America/New_York',
  Baltimore: 'America/New_York',
  Pittsburgh: 'America/New_York',
  Charlotte: 'America/New_York',
  Orlando: 'America/New_York',
  Tampa: 'America/New_York',
  Cleveland: 'America/New_York',
  // United States, central
  Dallas: 'America/Chicago',
  Houston: 'America/Chicago',
  Austin: 'America/Chicago',
  'San Antonio': 'America/Chicago',
  Nashville: 'America/Chicago',
  Minneapolis: 'America/Chicago',
  'Kansas City': 'America/Chicago',
  'New Orleans': 'America/Chicago',
  Milwaukee: 'America/Chicago',
  Memphis: 'America/Chicago',
  // United States, mountain and pacific
  'Salt Lake City': 'America/Denver',
  Albuquerque: 'America/Denver',
  Seattle: 'America/Los_Angeles',
  'San Francisco': 'America/Los_Angeles',
  'San Diego': 'America/Los_Angeles',
  'San Jose': 'America/Los_Angeles',
  Portland: 'America/Los_Angeles',
  Sacramento: 'America/Los_Angeles',
  'Las Vegas': 'America/Los_Angeles',
  // Canada
  Montreal: 'America/Toronto',
  Ottawa: 'America/Toronto',
  Calgary: 'America/Edmonton',
  // Europe
  Frankfurt: 'Europe/Berlin',
  Munich: 'Europe/Berlin',
  Hamburg: 'Europe/Berlin',
  Cologne: 'Europe/Berlin',
  Dusseldorf: 'Europe/Berlin',
  Stuttgart: 'Europe/Berlin',
  Milan: 'Europe/Rome',
  Turin: 'Europe/Rome',
  Naples: 'Europe/Rome',
  Florence: 'Europe/Rome',
  Barcelona: 'Europe/Madrid',
  Valencia: 'Europe/Madrid',
  Geneva: 'Europe/Zurich',
  Basel: 'Europe/Zurich',
  Bern: 'Europe/Zurich',
  Manchester: 'Europe/London',
  Edinburgh: 'Europe/London',
  Birmingham: 'Europe/London',
  Glasgow: 'Europe/London',
  Leeds: 'Europe/London',
  Bristol: 'Europe/London',
  Cardiff: 'Europe/London',
  Belfast: 'Europe/London',
  Lyon: 'Europe/Paris',
  Marseille: 'Europe/Paris',
  Rotterdam: 'Europe/Amsterdam',
  'The Hague': 'Europe/Amsterdam',
  Gothenburg: 'Europe/Stockholm',
  Cork: 'Europe/Dublin',
  Krakow: 'Europe/Warsaw',
  Porto: 'Europe/Lisbon',
  // Africa and the Americas
  'Cape Town': 'Africa/Johannesburg',
  Durban: 'Africa/Johannesburg',
  Pretoria: 'Africa/Johannesburg',
  'Rio de Janeiro': 'America/Sao_Paulo',
  Guadalajara: 'America/Mexico_City',
  Monterrey: 'America/Mexico_City',
  Wellington: 'Pacific/Auckland',
};

export interface TimezoneOption {
  value: string;
  label: string;
  /**
   * City names of every IANA zone that collapsed into this option. Not shown,
   * but matched when searching, so a Toronto coach can type "toronto" and find
   * "Eastern Time" without being told they live in New York.
   */
  aliases: string[];
}

/**
 * Zones preferred as the stored identifier for their group, most preferred
 * first. COMMON_ZONES comes first so every value already in the database wins
 * its group and is never rewritten to some other city.
 *
 * Beyond those, these are recognisable business hubs. The choice is invisible
 * to users, because the label names the zone rather than the city, but a
 * sensible identifier keeps the database readable.
 */
const PREFERRED_REPRESENTATIVES: readonly string[] = [
  ...COMMON_ZONES,
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Africa/Lagos',
  'Africa/Cairo',
  'Africa/Nairobi',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Hong_Kong',
  'Asia/Manila',
  'Asia/Seoul',
  'Pacific/Auckland',
];

/** Read one `timeZoneName` part for a zone, or null when Intl rejects it. */
function timeZoneNamePart(
  timeZone: string,
  timeZoneName: 'longGeneric' | 'longOffset' | 'short',
  at: Date
): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName,
    }).formatToParts(at);
    return parts.find(p => p.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
}

/** UTC offset of a zone at an instant, in minutes. */
function offsetMinutes(timeZone: string, at: Date): number | null {
  try {
    const f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const p: Record<string, string> = {};
    for (const part of f.formatToParts(at)) p[part.type] = part.value;
    const asUtc = Date.UTC(
      Number(p.year),
      Number(p.month) - 1,
      Number(p.day),
      Number(p.hour) % 24,
      Number(p.minute)
    );
    return (asUtc - at.getTime()) / 60000;
  } catch {
    return null;
  }
}

/**
 * Instants used to fingerprint a zone's behaviour: the 1st and 15th of every
 * month across two years.
 *
 * Sampling matters because two zones can share an offset today and diverge in
 * six months. Europe/London and Asia/Kolkata are both listed as "GMT+05:30
 * apart" only in winter; London changes clocks in late March and India never
 * does. Comparing today's offset alone would call them equivalent.
 */
const SIGNATURE_INSTANTS: readonly Date[] = (() => {
  const year = new Date().getUTCFullYear();
  const out: Date[] = [];
  for (let m = 0; m < 24; m++) {
    for (const day of [1, 15]) out.push(new Date(Date.UTC(year, m, day, 12)));
  }
  return out;
})();

/** Offset in `+HH:MM` form for the label, spelled out even at zero. */
function offsetLabel(timeZone: string, at: Date): string | null {
  const raw = timeZoneNamePart(timeZone, 'longOffset', at);
  if (!raw) return null;
  // Intl returns a bare "GMT" for zero-offset zones. Spell it out so no label
  // ever shows "GMT" without a number beside it.
  return raw === 'GMT' ? 'GMT+00:00' : raw;
}

/** City part of an identifier, for search aliases. */
function cityOf(timeZone: string): string {
  return timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone;
}

/**
 * One option per distinct clock, plus a lookup from any IANA identifier to the
 * option that represents it.
 *
 * Two zones collapse into one option only when BOTH their behaviour signature
 * and their human-facing name match. Behaviour alone would merge Japan and
 * Korea, which are both UTC+9 with no daylight saving, and a Korean coach
 * should not be filed under "Japan Standard Time". Requiring the name too
 * keeps those apart while still collapsing the 15 zones ICU calls
 * "Eastern Time" into a single entry.
 *
 * Computed once at module load. Fingerprinting ~420 zones across 48 instants
 * is far too slow to repeat per render.
 */
const { options: builtOptions, byZone: builtByZone } = (() => {
  const at = new Date();

  // Intl.supportedValuesOf is ES2022. Guard anyway so an old runtime degrades
  // to the pinned list instead of rendering an empty picker.
  let raw: string[];
  try {
    raw = Intl.supportedValuesOf('timeZone');
  } catch {
    raw = [...COMMON_ZONES];
  }

  const candidates: string[] = [];
  const seen = new Set<string>();
  for (const zone of [...raw, ...EXTRA_ZONES, ...COMMON_ZONES]) {
    const value = IANA_RENAMES[zone] ?? zone;
    if (seen.has(value)) continue;
    if (!offsetLabel(value, at)) continue;
    seen.add(value);
    candidates.push(value);
  }

  // Group by name + behaviour.
  const groups = new Map<string, string[]>();
  for (const zone of candidates) {
    const name = timeZoneNamePart(zone, 'longGeneric', at) ?? zone;
    const signature = SIGNATURE_INSTANTS.map(i => offsetMinutes(zone, i)).join(
      ','
    );
    const key = `${name}|${signature}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(zone);
    else groups.set(key, [zone]);
  }

  const rank = new Map(PREFERRED_REPRESENTATIVES.map((z, i) => [z, i]));
  const options: TimezoneOption[] = [];
  const byZone: Record<string, string> = {};

  for (const [key, members] of groups) {
    const representative = [...members].sort((a, b) => {
      const ra = rank.get(a) ?? Number.MAX_SAFE_INTEGER;
      const rb = rank.get(b) ?? Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      // Prefer a shallower path, so America/Toronto beats
      // America/Indiana/Marengo when neither is preferred by name.
      const da = a.split('/').length;
      const db = b.split('/').length;
      if (da !== db) return da - db;
      return a.localeCompare(b);
    })[0];

    const name = key.slice(0, key.indexOf('|'));
    const offset = offsetLabel(representative, at);
    // UTC's generic name is also "GMT", which would read "GMT (GMT+00:00)".
    const displayName =
      LABEL_OVERRIDES[representative] ??
      (representative === 'UTC' ? 'Coordinated Universal Time' : name);
    const label = `${displayName} (${offset})`;

    options.push({
      value: representative,
      label,
      aliases: members.map(cityOf),
    });
    for (const member of members) byZone[member] = representative;
  }

  // Two clocks can share a name and today's offset yet differ in when they
  // change. Africa/Cairo and the EU's Eastern European Time zones both read
  // "Eastern European Time (GMT+03:00)" in summer but switch on different
  // dates. Naming the representative city is the only way a user can tell
  // them apart, so add it only where a label would otherwise repeat.
  const labelCounts = new Map<string, number>();
  for (const option of options) {
    labelCounts.set(option.label, (labelCounts.get(option.label) ?? 0) + 1);
  }
  for (const option of options) {
    if ((labelCounts.get(option.label) ?? 0) > 1) {
      const offsetStart = option.label.lastIndexOf(' (');
      const name = option.label.slice(0, offsetStart);
      const suffix = option.label.slice(offsetStart);
      option.label = `${name} - ${cityOf(option.value)}${suffix}`;
    }
  }

  // Attach the curated city names to whichever option represents their zone.
  const optionByValue = new Map(options.map(o => [o.value, o]));
  for (const [city, zone] of Object.entries(CITY_ALIASES)) {
    const representative = byZone[IANA_RENAMES[zone] ?? zone] ?? byZone[zone];
    const option = representative
      ? optionByValue.get(representative)
      : undefined;
    // Silently skip a city whose zone this runtime does not know, rather than
    // shipping a search term that matches nothing.
    if (option) option.aliases.push(city);
  }

  options.sort((a, b) => a.label.localeCompare(b.label));
  return { options, byZone };
})();

/** One option per distinct clock, sorted by label. */
export const TIMEZONE_OPTIONS: readonly TimezoneOption[] = builtOptions;

/** Any IANA identifier mapped to the option value that represents it. */
const REPRESENTATIVE_BY_ZONE: Record<string, string> = builtByZone;

/** Look up an option by its representative identifier. */
const OPTION_BY_VALUE: Record<string, TimezoneOption> = Object.fromEntries(
  TIMEZONE_OPTIONS.map(o => [o.value, o])
);

/**
 * The options a picker offers: ALLOWED_ZONES, in the order written there.
 *
 * Flat rather than grouped. Eleven entries need no group headers, and the
 * previous region groups only existed to make 176 options navigable.
 *
 * Each zone resolves through the same grouping as everything else, so an entry
 * carries the label and the search aliases of every city that collapsed into
 * it. `Europe/Paris` therefore covers Berlin and Frankfurt, and
 * `America/New_York` covers Toronto and Boston.
 */
export const TIMEZONE_SELECT_DATA: readonly {
  value: string;
  label: string;
}[] = (() => {
  const seen = new Set<string>();
  const items: { value: string; label: string }[] = [];
  for (const zone of ALLOWED_ZONES) {
    const representative = REPRESENTATIVE_BY_ZONE[zone];
    if (!representative || seen.has(representative)) continue;
    const option = OPTION_BY_VALUE[representative];
    if (!option) continue;
    seen.add(representative);
    items.push({ value: option.value, label: option.label });
  }
  return items;
})();

/** Representatives of every offered zone, for the out-of-list check. */
const OFFERED_VALUES: ReadonlySet<string> = new Set(
  TIMEZONE_SELECT_DATA.map(o => o.value)
);

/**
 * Whether a stored value is one a picker still offers.
 *
 * A row written before the list was shortened may hold a zone that is no
 * longer a choice, such as `Asia/Tokyo`. Those values stay valid and stay
 * rendered; the picker appends the current one so the field never blanks. See
 * `TimezoneSelect`.
 */
export function isOfferedTimezone(value: string | null | undefined): boolean {
  if (!value) return false;
  const normalized = normalizeTimezone(value);
  return Boolean(normalized && OFFERED_VALUES.has(normalized));
}

/**
 * Resolve a stored value to the option that represents its clock.
 *
 * A row may hold any IANA identifier, including a legacy name such as
 * `Asia/Calcutta` or a city that collapsed into another option such as
 * `Europe/Berlin`. Without this the Select would find no matching option and
 * render blank, which reads as data loss.
 */
export function normalizeTimezone(
  value: string | null | undefined
): string | null {
  if (!value) return null;
  const renamed = IANA_RENAMES[value] ?? value;
  return (
    REPRESENTATIVE_BY_ZONE[renamed] ?? REPRESENTATIVE_BY_ZONE[value] ?? null
  );
}

/**
 * Familiar short codes for the markets Medley actually uses.
 *
 * Deliberately hand-written and deliberately short. Deriving codes from the
 * zone names automatically was tested and rejected: across the 176 zones it
 * produces only 89 codes, with 33 reused. "MT" would mean Mountain Time,
 * Malaysia Time or Myanmar Time, and "CT" would mean Central Time, Cuba Time
 * or Chile Time. That is a silent, familiar-looking wrong answer, which is the
 * worst kind for a scheduling tool. ICU declines to give short codes for
 * non-US zones for the same reason.
 *
 * A zone missing from this map falls back to its offset, which is never
 * ambiguous. So the cost of an omission is verbosity, not error.
 *
 * These are provisional, taken from the 2026-09-04 planning meeting. Replace
 * them with the team's curated list when it arrives, and keep COMMON_ZONES in
 * step so the quick-select and these codes describe the same set.
 *
 * Keys may be any identifier; they resolve through the same grouping as
 * everything else.
 */
const ZONE_ABBREVIATIONS: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  // Arizona keeps one offset all year, so "MST" is literally correct. It is
  // still wrong to show, because it sits one row from "MT" and the two read as
  // the same thing. "AZ" cannot be confused with anything.
  'America/Phoenix': 'AZ',
  'America/Anchorage': 'AKT',
  'Pacific/Honolulu': 'HST',
  // No settled DST-neutral code exists for Britain: "GMT" is the winter name
  // only, and calling it GMT year round is the error this change fixes. Jess
  // says "UK time", so use that.
  'Europe/London': 'UK',
  'Europe/Paris': 'CET',
  'Europe/Athens': 'EET',
  'Asia/Kolkata': 'IST',
  'Asia/Dubai': 'GST',
  'Asia/Singapore': 'SGT',
  'Asia/Hong_Kong': 'HKT',
  // China's standard code is CST, which also means US Central Standard Time.
  // Spell it out rather than ship a code with two meanings.
  'Asia/Shanghai': 'China',
  'Asia/Tokyo': 'JST',
  // "AEST" is the winter name and "AEDT" the summer one, the same trap as
  // "EST" versus "ET". Australia's DST-neutral code is "AET".
  'Australia/Sydney': 'AET',
  'Pacific/Auckland': 'NZT',
};

/**
 * Drop the trailing `(GMT+HH:MM)` from a label, leaving the zone name.
 *
 * Exported because the picker's own filter needs the same treatment for the
 * sentinel options it adds, and the two must agree.
 */
export function stripOffsetSuffix(label: string): string {
  return label.replace(/\s*\(GMT[^)]*\)\s*/g, ' ').trim();
}

/**
 * Everything that should match an option when searching.
 *
 * The label, every member city, the identifier, the short code, and any extra
 * words in EXTRA_SEARCH_TERMS.
 *
 * The short code matters more than it looks. The team thinks in codes, and no
 * label contains its own: "India Standard Time" does not contain "IST", so
 * typing the code an admin has in their head used to find nothing.
 */
export function timezoneSearchTerms(value: string): string {
  const option = OPTION_BY_VALUE[value];
  if (!option) return value;
  // Strip the trailing offset. Every label ends in "(GMT+05:30)" or similar,
  // so leaving it in makes "gmt" match all eleven options and hides the one
  // the user wants, which is Britain.
  const name = stripOffsetSuffix(option.label);
  const code = ZONE_ABBREVIATIONS[option.value] ?? '';
  const extra = EXTRA_SEARCH_TERMS[option.value] ?? '';
  return `${name} ${option.aliases.join(' ')} ${option.value} ${code} ${extra}`.toLowerCase();
}

/**
 * Value to hand a Mantine `Select`.
 *
 * Prefers the normalized identifier so a legacy stored name still selects its
 * option. Falls back to the raw value rather than null, so an unrecognised
 * zone shows as-is instead of silently blanking a field the user did fill in.
 */
export function toSelectValue(value: string | null | undefined): string | null {
  if (!value) return null;
  return normalizeTimezone(value) ?? value;
}

/**
 * MED-1365 QA round 3: the zone name on the scheduling screens, for the
 * instant `at`: the generic name, then the abbreviation in use then, for
 * example "Eastern Time (EDT)", or "Eastern Time (EST)" in winter. Never a
 * city. The picker labels use the same overrides: "Arizona Time (MST)" and
 * "Coordinated Universal Time (UTC)". Never throws: an old browser without
 * the generic name option gets the zone id.
 */
export function getZoneDisplayLabel(timeZone: string, at: Date): string {
  const representative = normalizeTimezone(timeZone) ?? timeZone;
  const name =
    LABEL_OVERRIDES[representative] ??
    (representative === 'UTC'
      ? 'Coordinated Universal Time'
      : (timeZoneNamePart(timeZone, 'longGeneric', at) ?? timeZone));
  const abbr = timeZoneNamePart(timeZone, 'short', at);
  return abbr && abbr !== name ? `${name} (${abbr})` : name;
}

/** Label for a stored value. Falls back to the raw identifier. */
export function getTimezoneLabel(value: string | null | undefined): string {
  if (!value) return '';
  const normalized = normalizeTimezone(value);
  return normalized
    ? (OPTION_BY_VALUE[normalized]?.label ?? normalized)
    : value;
}

/**
 * Familiar short code for a zone, or null when there is no safe one.
 *
 * Resolves the value first, so a row holding `Europe/Berlin` gets `CET` from
 * the option that represents it.
 */
export function getZoneAbbreviation(
  value: string | null | undefined
): string | null {
  if (!value) return null;
  const direct = ZONE_ABBREVIATIONS[value];
  if (direct) return direct;
  const normalized = normalizeTimezone(value);
  return normalized ? (ZONE_ABBREVIATIONS[normalized] ?? null) : null;
}
